import React, { useState, useEffect } from "react";

import {
  useGetLifePlansQuery,
  useGetLongTermDisabilityPlansQuery,
  useGetShortTermDisabilityPlansQuery,
  useGetFormattedEmployeeDataQuery,
} from "EmployerApplication/api";
import { formatUpdatedEmployeeProfile, formatEmployeeInfo } from "EmployerApplication/Components/ComprehensiveCostEstimation/EmployeeDetailsBody";
import costCalculationBuilder from "EmployerApplication/costCalculationBuilder";
import { getAllSelectedPlans } from "EmployerApplication/Selectors";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { isNil, toArray } from "lodash";
import { getCurrentPath } from "QuotePage/Selectors";
import { isRenewal } from "QuotePage/urls";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";

import { getCart } from "Common/CartService";
import { CONTRIBUTION_TYPES, TIERED_CONTRIBUTION_ROLES } from "Common/Constants";
import { fetchPricing } from "Common/PlanLoader/api";

import EditEmployeePriceBreakdownForm from "./EditEmployeePriceBreakdownForm";

const focusOnErrors = createDecorator();

const ENROLLING_TYPE = {
  medical: "is_enrolling",
  dental: "is_enrolling_dental",
  vision: "is_enrolling_vision",
  life: "is_enrolling_life",
};

const defaultEnrolling = (employeeProfiles) =>
  employeeProfiles.map((employee) => {
    const dependents = [];
    if (employee.spouse_age) {
      dependents.push({
        is_enrolling: true,
        is_enrolling_dental: true,
        is_enrolling_vision: true,
      });
    }
    if (employee.children) {
      employee.children.forEach(() => {
        dependents.push({
          is_enrolling: true,
          is_enrolling_dental: true,
          is_enrolling_vision: true,
        });
      });
    }

    return {
      is_enrolling: true,
      is_enrolling_dental: true,
      is_enrolling_vision: true,
      is_enrolling_life: !!employee.full_time,
      dependents,
    };
  });

export const EditEmployeePriceBreakdownBody = ({ contributionType: initialContributionsType, contributions: initialContributions }) => {
  const employeeProfiles = useSelector((state) => state.employeeProfile);
  const companyIDToken = useSelector((state) => state.companyProfile?.idToken);
  const cart = useSelector((state) => state.cart);
  const companyZipCode = useSelector((state) => state.companyProfile?.zip_code_number);

  const targetEffectiveDate = useSelector((state) => state.companyProfile?.targetEffectiveDate);

  const currentPath = getCurrentPath();
  const beforeAddEmployeesStep = /(coverage-options|company-information)/.test(currentPath);

  const selectedLifePlanId = cart.life;
  const useEmployeeForms = isRenewal() || !beforeAddEmployeesStep;
  const { data: lifePlanData } = useGetLifePlansQuery(
    {
      companyIDToken,
      useEmployeeForms,
      companyZipCode,
      targetEffectiveDate,
      employeeProfile: employeeProfiles,
    },

    { skip: !selectedLifePlanId },
  );

  const lifePlans = lifePlanData?.results;
  const lifePlan = (lifePlans || []).find((plan) => plan.id == selectedLifePlanId);

  const { medicalPlans, dentalPlan: initialDentalPlan, visionPlan: initialVisionPlan } = useSelector(getAllSelectedPlans);

  const [contributionType, setContributionType] = useState(initialContributionsType);

  const [contributions, setContributions] = useState(
    initialContributions.map((contribution) => {
      if (contributionType == CONTRIBUTION_TYPES.percentage) {
        return {
          employee_contribution: contribution.employeePercentage,
          dependent_contribution: contribution.dependentPercentage,
        };
      }
      return {
        employee_contribution: contribution.employeeFixedAmount,
        dependent_contribution: contribution.dependentFixedAmount,
      };
    }),
  );

  const { data } = useGetFormattedEmployeeDataQuery();
  const formattedEmployeeData = data?.formatted_employee_data || [];

  const employees = formattedEmployeeData.length > 0 && !beforeAddEmployeesStep ? formattedEmployeeData : employeeProfiles;
  const [plans, setPlans] = useState(medicalPlans || []);

  const [dentalPlan, setDentalPlans] = useState(initialDentalPlan ? JSON.parse(JSON.stringify(initialDentalPlan)) : null);

  const [visionPlan, setVisionPlans] = useState(initialVisionPlan ? JSON.parse(JSON.stringify(initialVisionPlan)) : null);

  const [enrolling, setEnrolling] = useState(defaultEnrolling(employees));

  const recievePricing = (plans, pricing) => {
    const newPlans = plans.map((plan) => {
      let newPlan = plan;
      if (pricing.averageMonthlyPremiums[plan.id]) {
        newPlan = {
          ...plan,
          prices_loading: false,
          average_monthly_premium_per_person: parseInt(pricing.averageMonthlyPremiums[plan.id][3].toFixed(2), 10),

          average_monthly_premium: parseInt(pricing.averageMonthlyPremiums[plan.id][0].toFixed(2), 10),

          employee_costs: toArray(pricing.employeeCosts[plan.id]),
          total_cost: parseInt(pricing.totalCosts[plan.id].toFixed(2), 10),
        };
      }
      return newPlan;
    });
    return newPlans;
  };

  useEffect(() => {
    const fetchPricingData = async () => {
      const planIds = plans.map((plan) => plan.id);
      const pricing = await fetchPricing(employees, planIds, companyIDToken).then((response) => response.json());
      const newPlans = recievePricing(plans, {
        planIDs: pricing.plan_ids,
        averageMonthlyPremiums: pricing.average_monthly_premiums,
        employeeCosts: pricing.employee_costs,
        totalCosts: pricing.total_costs,
      });

      setPlans(newPlans);

      if (dentalPlan) {
        const dentalResponse = await fetchWithCSRFToken(`/small-group/select-plan/ajax-selected-dental-plan?company_id_token=${companyIDToken}`, {
          method: "POST",
          body: JSON.stringify({
            employee_profile: JSON.stringify(employees),
          }),
        }).then((response) => response.json());
        setDentalPlans(dentalResponse.results[0]);
      }

      if (visionPlan) {
        const visionResponse = await fetchWithCSRFToken(`/small-group/select-plan/ajax-selected-vision-plan?company_id_token=${companyIDToken}`, {
          method: "POST",
          body: JSON.stringify({
            employee_profile: JSON.stringify(employees),
          }),
        }).then((response) => response.json());
        setVisionPlans(visionResponse.results[0]);
      }
    };

    fetchPricingData();
  }, []);

  useEffect(() => {
    setEnrolling(defaultEnrolling(employees));
  }, [employees]);

  const [employeeRoles, setEmployeeRoles] = useState(employees.map((employee) => employee.role || initialContributions[0].role));

  const [selectedMedicalPlans, setSelectedMedicalPlans] = useState(employees.map(() => medicalPlans[0].id));

  const defaultFullTimeEmployees = employees.map((employee) => {
    if (employee.full_time === null || employee.full_time === undefined) {
      return { ...employee, full_time: true };
    }
    return employee;
  });

  const [fullTimeEmployees, setFullTimeEmployees] = useState(defaultFullTimeEmployees.map((employee) => employee.full_time));

  const { short_term_disability: shortTermPlanId, long_term_disability: longTermPlanId } = useSelector(getCart);

  const { data: availableShortTermPlans } = useGetShortTermDisabilityPlansQuery({ companyIDToken }, { skip: !shortTermPlanId });

  const { data: availableLongTermPlans } = useGetLongTermDisabilityPlansQuery({ companyIDToken }, { skip: !longTermPlanId });

  const shortTermPlan = availableShortTermPlans?.results?.find((plan) => plan.id === shortTermPlanId);

  const longTermPlan = availableLongTermPlans?.results?.find((plan) => plan.id === longTermPlanId);

  const shortTermEmployeeCosts = shortTermPlan?.employee_costs_include_part_time;
  const longTermEmployeeCosts = longTermPlan?.employee_costs_include_part_time;

  const handleEnrollingToggle = (type, employee, dependentIndex) => {
    const _enrolling = [...enrolling];
    if (type === "fullTime") {
      fullTimeEmployees[employee] = !fullTimeEmployees[employee];
      setFullTimeEmployees([...fullTimeEmployees]);
      _enrolling[employee].is_enrolling_life = fullTimeEmployees[employee];
    } else if (isNil(dependentIndex)) {
      _enrolling[employee][ENROLLING_TYPE[type]] = !_enrolling[employee][ENROLLING_TYPE[type]];

      if (!_enrolling[employee][ENROLLING_TYPE[type]]) {
        _enrolling[employee].dependents.forEach((dependent) => {
          dependent[ENROLLING_TYPE[type]] = false;
        });
      }
    } else {
      _enrolling[employee].dependents[dependentIndex][ENROLLING_TYPE[type]] = !_enrolling[employee].dependents[dependentIndex][ENROLLING_TYPE[type]];
    }
    setEnrolling([..._enrolling]);
  };

  const handleRoleChange = (role, employee) => {
    const _employeeRoles = [...employeeRoles];
    _employeeRoles[employee] = role;

    setEmployeeRoles([..._employeeRoles]);
  };

  const handleMedicalPlanChange = (medicalPlanId, employee) => {
    const _selectedMedicalPlans = [...selectedMedicalPlans];
    _selectedMedicalPlans[employee] = medicalPlanId;

    setSelectedMedicalPlans([..._selectedMedicalPlans]);
  };

  const contributionOptions = {};
  initialContributions.forEach((contribution) => {
    contributionOptions[TIERED_CONTRIBUTION_ROLES[contribution.role]] = contribution.role;
  });

  const formattedEmployees = formatUpdatedEmployeeProfile(employees.map((ee) => formatEmployeeInfo(ee))).map((ee, index) => {
    const hasSpouse = !!ee.spouse_age;
    return {
      ...ee,
      ...enrolling[index],
      role: employeeRoles[index],
      selected_medical_plan: selectedMedicalPlans[index],
      dependents: ee.dependents.map((dependent, dependentIndex) => ({
        ...dependent,
        ...enrolling[index]?.dependents[dependentIndex],
        type: dependentIndex == 0 && hasSpouse ? "Spouse" : "Child",
      })),

      spouse_enrollment: hasSpouse ? enrolling[index]?.dependents[0] : undefined,
      children_enrollment: enrolling[index]?.dependents.slice(hasSpouse ? 1 : 0),
      full_time: fullTimeEmployees[index],
    };
  });

  const rows = costCalculationBuilder({
    employeeProfiles: formattedEmployees,
    contributions: contributions.map((contribution, index) => {
      if (contributionType == CONTRIBUTION_TYPES.percentage) {
        return {
          ...initialContributions[index],
          employeePercentage: contribution.employee_contribution,
          dependentPercentage: contribution.dependent_contribution,
        };
      }
      return {
        ...initialContributions[index],
        employeeFixedAmount: contribution.employee_contribution,
        dependentFixedAmount: contribution.dependent_contribution,
      };
    }),
    contributionType,
    medicalPlans: plans,
    dentalPlan,
    visionPlan,
    lifePlan,
    fullTimeEmployees,
    shortTermEmployeeCosts,
    longTermEmployeeCosts,
  });

  const costFormattedEmployees = rows.employeesBreakdowns.map((ee, i) => ({
    ...ee,
    ...formattedEmployees[i],
    dependents: ee.individualDependentCosts?.map((dependent, dependentIndex) => ({
      ...dependent,
      ...formattedEmployees[i].dependents[dependentIndex],
    })),
  }));

  const initialValues = {
    contributions,
    employees: costFormattedEmployees,
  };

  return (
    <Form onSubmit={() => {}} decorators={[focusOnErrors]} initialValues={initialValues} mutators={{ ...arrayMutators }}>
      {(props) => (
        <EditEmployeePriceBreakdownForm
          {...props}
          formEmployeeProfiles={costFormattedEmployees}
          totals={rows.companyTotals}
          medicalPlans={plans}
          dentalPlan={dentalPlan}
          visionPlan={visionPlan}
          handleEnrollingToggle={handleEnrollingToggle}
          handleRoleChange={handleRoleChange}
          handleMedicalPlanChange={handleMedicalPlanChange}
          contributionOptions={contributionOptions}
          contributionType={contributionType}
          setContributionType={setContributionType}
          contributions={contributions}
          setContributions={setContributions}
          fullTimeEmployees={fullTimeEmployees}
        />
      )}
    </Form>
  );
};

export default EditEmployeePriceBreakdownBody;
